import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { LanguageCode, PRIMARY_LANGUAGE, ALL_LANGUAGES, getLanguageInfo } from "@/shared/config/languages";
import { getUiTranslation } from "@/shared/config/translations";
import { useFactoryStore } from "@/core/hooks/useFactory";
import { supabase } from "@/integrations/supabase/client";

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  getTranslation: (translations: Record<string, string> | null, fallback: string) => string;
  t: (key: string, fallback: string) => string;
  availableLanguages: typeof ALL_LANGUAGES[number][];
  secondaryLanguage: LanguageCode | null;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { selectedFactoryId } = useFactoryStore();
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    const saved = localStorage.getItem("app-language");
    return (saved as LanguageCode) || PRIMARY_LANGUAGE;
  });
  const [secondaryLanguage, setSecondaryLanguage] = useState<LanguageCode | null>(null);

  // Fetch factory's secondary language when factory changes
  useEffect(() => {
    const fetchFactoryLanguage = async () => {
      if (!selectedFactoryId) {
        setSecondaryLanguage(null);
        return;
      }

      // Query public.factories for secondary_language (not in core schema yet)
      const { data, error } = await supabase
        .from("factories")
        .select("secondary_language")
        .eq("id", selectedFactoryId)
        .maybeSingle();

      if (!error && data?.secondary_language) {
        const langInfo = getLanguageInfo(data.secondary_language);
        if (langInfo) {
          setSecondaryLanguage(data.secondary_language as LanguageCode);
          // Restore saved language if it matches the secondary language
          const saved = localStorage.getItem("app-language");
          if (saved === data.secondary_language) {
            setLanguageState(saved as LanguageCode);
          }
        } else {
          setSecondaryLanguage(null);
        }
      } else {
        setSecondaryLanguage(null);
      }
    };

    fetchFactoryLanguage();
  }, [selectedFactoryId]);

  // Reset to primary language if current language is no longer available
  useEffect(() => {
    if (language !== PRIMARY_LANGUAGE && secondaryLanguage && language !== secondaryLanguage) {
      setLanguageState(PRIMARY_LANGUAGE);
      localStorage.setItem("app-language", PRIMARY_LANGUAGE);
    }
  }, [secondaryLanguage, language]);

  const availableLanguages = secondaryLanguage
    ? ALL_LANGUAGES.filter(l => l.code === PRIMARY_LANGUAGE || l.code === secondaryLanguage)
    : ALL_LANGUAGES.filter(l => l.code === PRIMARY_LANGUAGE);

  const handleSetLanguage = (lang: LanguageCode) => {
    // Only allow setting to primary or secondary language
    if (lang === PRIMARY_LANGUAGE || lang === secondaryLanguage) {
      setLanguageState(lang);
      localStorage.setItem("app-language", lang);
    }
  };

  const getTranslation = (translations: Record<string, string> | null, fallback: string): string => {
    if (!translations) return fallback;
    return translations[language] || translations[PRIMARY_LANGUAGE] || fallback;
  };

  const t = (key: string, fallback: string): string => {
    return getUiTranslation(language, key, fallback);
  };

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage: handleSetLanguage,
      getTranslation,
      t,
      availableLanguages,
      secondaryLanguage
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}
